﻿import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  Grid,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import { faArrowRight, faExclamationCircle, faRefresh } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  DateFormControl,
  Hint,
  InputFormControl,
  MultipleSelectFormControl,
  NumberFormControl,
  TextareaFormControl,
  type Option,
} from 'BrokerTsLib/src/components'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import type { InsuranceDto, InsuranceParamsLeasingDto, VehicleValuationDto } from 'BrokerTsLib/src/dtos'
import { Selection } from 'BrokerTsLib/src/enums'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import * as yup from 'yup'

import { useGetVehicleAveragePrice } from '@/hooks/useVehicle'
import { useSaveChangeNew } from '@/hooks/useWizard'

// Hodnota důvodu „Jiné" – po výběru se zobrazí pole pro vlastní text
const OTHER_REASON = 'Other'

// „Cena vozidla z" – dvě volby na formuláři (PriceOriginId)
const PRICE_ORIGIN_ACCORDING_HOLDER = Selection.AccordingPolicyHolder // dle pojistníka = pojistná cena
const PRICE_ORIGIN_VALUATION = Selection.VehicleValuation // dle ocenění = časová cena

// Koeficient DPH pro přepočet pojistné částky při přepnutí plátce/neplátce (shodně s krokem 1)
const VAT_COEF = 1.21

const schema = yup.object({
  isVatPayer: yup.boolean().required(),
  vin: yup.string().required('Zadejte VIN.').max(17, 'VIN může mít maximálně 17 znaků.'),
  engineVolume: yup.number().nullable().optional(),
  firstRegistrationDate: yup.date().nullable().optional(),
  sumInsured: yup.number().nullable().optional(),
  priceOriginId: yup.number().required(),
  extraEquipmentPrice: yup.number().nullable().optional(),
  reasons: yup.array().of(yup.string().required()).min(1, 'Vyberte alespoň jeden důvod.').required(),
  otherReason: yup.string().nullable().when('reasons', {
    is: (reasons?: string[]) => reasons?.includes(OTHER_REASON) ?? false,
    then: s => s.required('Zadejte důvod.'),
    otherwise: s => s.optional(),
  }),
})

type FormValues = yup.InferType<typeof schema>

// TODO: finální číselník důvodů bude upřesněn zadáním – zatím pracovní hodnoty
const reasonOptions: Option[] = [
  { value: 'IncreaseSumInsuredEquipment', label: 'Změna pojistné částky z důvodu připojištění výbavy' },
  { value: 'ChangePayerType', label: 'Změna pojistné částky z důvodu změny plátce/neplátce DPH' },
  { value: 'IncreaseSumInsured', label: 'Změna pojistné částky' },
  { value: 'DeductibleChange', label: 'Změna spoluúčasti havarijního pojištění' },
  { value: 'ChangeSeatLimit', label: 'Změna limitu sedadel' },
  { value: 'ChangeBaggageLimit', label: 'Změna limitu zavazadel' },
  { value: 'ChangeLiabilitiesLimit', label: 'Změna limitu povinného ručení' },
  { value: 'ChangeInsurer', label: 'Změna pojistitele' },
  { value: 'ChangeBrandPackage', label: 'Změna balíčku značkového pojištění' },
  { value: 'ChangeAssistance', label: 'Změna asistence' },
  { value: 'AddedGlass', label: 'Připojištění skla' },
  { value: 'AddedSeats', label: 'Připojištění sedadel' },
  { value: 'AddedBaggage', label: 'Připojištění zavazadel' },
  { value: 'AddedHav', label: 'Připojištění havarijního pojištění' },
  { value: 'AddedLiability', label: 'Připojištění povinného ručení' },
  { value: 'ChangeHavVariant', label: 'Změna varianty havarijního pojištění' },
  { value: 'RiskTermination', label: 'Změna z důvodu ukončení rizika' },
  { value: 'ChangePackage', label: 'Změna balíčku pojištění' },
  { value: 'ChangeVin', label: 'Změna VIN' },
  { value: 'ChangeEngineVolume', label: 'Změna objemu' },
  { value: 'ChangeFirstRegistrationDate', label: 'Změna data první registrace' },
  { value: 'ChangeUsage', label: 'Změna užití' },
  { value: 'Other', label: 'Jiné' }
]

/** Popisek + hodnota v šedém záhlaví modálu. */
const HeaderValue = ({ label, value }: { label: string; value?: string | null }) => (
  <Box px={4}>
    <Text fontSize="xs" color="gray.500">
      {label}
    </Text>
    <Text fontWeight="bold">{value ?? '—'}</Text>
  </Box>
)

/** Dvojice checkboxů chovající se jako přepínač (viz UI návrh – nejde o klasické radio). */
const CheckboxPair = ({
  value,
  onChange,
  trueLabel,
  falseLabel,
  falseDisabled = false,
}: {
  value: boolean
  onChange: (value: boolean) => void
  trueLabel: string
  falseLabel: string
  /** Zakáže pouze druhou (false) volbu – např. „Časová cena", dokud se nenačte ocenění. */
  falseDisabled?: boolean
}) => (
  <HStack gap={8}>
    <Checkbox isChecked={value} onChange={() => onChange(true)}>
      <Text fontWeight={value ? 'bold' : 'normal'}>{trueLabel}</Text>
    </Checkbox>
    <Checkbox isChecked={!value} isDisabled={falseDisabled} onChange={() => onChange(false)}>
      <Text fontWeight={!value ? 'bold' : 'normal'}>{falseLabel}</Text>
    </Checkbox>
  </HStack>
)

/**
 * Proces „Změna novou" (VWFS) – z detailu smlouvy založí novou kalkulaci vycházející z původní smlouvy.
 * Osoby a vozidlo se čtou z entity Insurance (GetInsurance), protože každá smlouva může mít jiné hodnoty
 * než InsuranceParams. Uživatel hodnoty zkontroluje/upraví a pokračuje na kalkulaci.
 */
export const ChangeNewButton = ({
  insurance,
  insuranceParams,
  leasingContractNumber,
  callback,
}: {
  insurance: InsuranceDto
  insuranceParams: InsuranceParamsLeasingDto
  leasingContractNumber?: string | null
  callback: () => void
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const navigate = useNavigate()
  const { mutateAsync: saveChangeNew, isPending } = useSaveChangeNew()
  const { mutateAsync: getAveragePriceAsync } = useGetVehicleAveragePrice()

  const vehicle = insurance.vehicle ?? insuranceParams.vehicle
  const client = insurance.client
  const clientName = client?.companyName ?? ([client?.name, client?.surname].filter(Boolean).join(' ') || null)

  // Časová cena (ocenění) – načítá se asynchronně (průměrovaná cena, bez výběru stupně výbavy).
  // Response z ocenění držíme celý ve state; podle plátcovství DPH pak zobrazujeme cenu s/bez DPH.
  const [valuation, setValuation] = useState<VehicleValuationDto | null>(null)
  const [valuationError, setValuationError] = useState<string | null>(null)
  const [isLoadingTimePrice, setIsLoadingTimePrice] = useState(false)

  const initialValues = (): FormValues => ({
    isVatPayer: client?.isVatPayer ?? true,
    vin: vehicle?.vin ?? '',
    engineVolume: vehicle?.objem ?? null,
    firstRegistrationDate: vehicle?.firstRegistrationDate
      ? new Date(vehicle.firstRegistrationDate)
      : null,
    sumInsured: vehicle?.price ?? null,
    priceOriginId: PRICE_ORIGIN_ACCORDING_HOLDER,
    extraEquipmentPrice: null,
    reasons: [],
    otherReason: null,
  })

  const methods = useForm<FormValues>({
    defaultValues: initialValues(),
    resolver: yupResolver(schema),
    mode: 'onChange',
  })

  const { isVatPayer, vin, priceOriginId, sumInsured, extraEquipmentPrice, reasons } = methods.watch()
  const showOtherReason = reasons?.includes(OTHER_REASON) ?? false
  const useTimePrice = priceOriginId === PRICE_ORIGIN_VALUATION

  // Přepočet (ocenění) nabídneme jen když se změnily odesílané údaje vozidla – VIN, objem, datum 1. reg.
  const { dirtyFields } = methods.formState
  const valuationInputsChanged = !!(dirtyFields.vin || dirtyFields.engineVolume || dirtyFields.firstRegistrationDate)

  // Plátce DPH → počítá se s cenou bez DPH; neplátce → s cenou včetně DPH. Přepnutí DPH tedy mění
  // jak zobrazenou časovou cenu, tak popisek (s/bez DPH) – obě hodnoty máme z jednoho volání ve state.
  const timePrice = valuation
    ? (isVatPayer ? valuation.vehicleMarketPrice : valuation.vehicleMarketPriceWithTax)
    : null
  // Plátce DPH → částky bez DPH; neplátce → s DPH. Používá se v popiscích časové i pojistné částky.
  const vatLabel = isVatPayer ? 'bez DPH' : 's DPH'

  // Ocenění vozidla – průměrovaná cena (bez výběru stupně výbavy). Posílají se aktuální hodnoty
  // z formuláře (VIN, datum 1. registrace) – ve výchozím stavu tedy původní vozidlo, po úpravě nové.
  // Nájezd km není na formuláři, bere se z vozidla. Sdílené pro autoload i tlačítko přepočtu.
  const runValuation = useCallback(() => {
    const valuationVin = methods.getValues('vin')
    if (!valuationVin) return

    setValuation(null)
    setValuationError(null)
    setIsLoadingTimePrice(true)

    getAveragePriceAsync({
      vin: valuationVin,
      mileage: vehicle?.mileage ?? null,
      registrationDate: methods.getValues('firstRegistrationDate'),
    })
      .then(({ data }) => {
        if (data?.error) {
          setValuationError(data.error)
          setValuation(null)
        } else {
          setValuation(data)
          setValuationError(null)
        }
      })
      .catch(() => setValuationError('Ocenění vozidla se nezdařilo.'))
      .finally(() => setIsLoadingTimePrice(false))
  }, [methods, vehicle?.mileage, getAveragePriceAsync])

  // Po otevření modálu spustit ocenění vozidla
  useEffect(() => {
    if (isOpen) runValuation()
  }, [isOpen, runValuation])

  // Pojistná částka pro kalkulaci = zvolená cena (pojistná/časová) + mimořádná výbava
  const calculationBase = useTimePrice ? timePrice : sumInsured
  const calculationAmount =
    calculationBase != null ? calculationBase + (extraEquipmentPrice ?? 0) : null

  const handleOpen = () => {
    methods.reset(initialValues())
    onOpen()
  }

  const handleClose = () => {
    methods.reset(initialValues())
    onClose()
  }

  const onSubmit = async (values: FormValues) => {
    try {
      const res = await saveChangeNew({
        originalInsuranceId: insurance.insuranceId!,
        isVatPayer: values.isVatPayer,
        vin: values.vin,
        engineVolume: values.engineVolume,
        firstRegistrationDate: values.firstRegistrationDate
          ? formatDate(values.firstRegistrationDate)
          : null,
        sumInsured: values.sumInsured,
        priceOriginId: values.priceOriginId,
        extraEquipmentPrice: values.extraEquipmentPrice,
        reasons: values.reasons,
        otherReason: values.reasons.includes(OTHER_REASON) ? values.otherReason ?? null : null,
      })
      callback()
      handleClose()
      // TODO: až vznikne samostatná VWFS route, přesměrovat na ni; zatím klasický leasing krok 1
      navigate(`/leasing/steps/1/${res.insuranceParamsId}`)
    } catch {
      methods.setError('root', { message: 'Založení změny novou se nezdařilo.' })
    }
  }

  return (
    <>
      <Button variant="outline" onClick={handleOpen}>
        Změna novou
      </Button>

      <Modal isOpen={isOpen} onClose={handleClose} size="3xl" closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pb={0}>
            <Text color="primary.500">Změna novou</Text>
            <Text fontSize="sm" fontWeight="normal" mt={1}>
              Nová kalkulace vychází z původní smlouvy. Zkontrolujte a upravte vstupní parametry —
              po vytvoření pokračujte na kalkulaci.
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
              <ModalBody>
                <Stack gap={6}>
                  <Grid templateColumns="repeat(4, 1fr)" bgColor="gray.100" py={3} sx={{ '& > div + div': { borderLeft: '1px solid', borderColor: 'gray.300' } }}>
                    <HeaderValue label="Číslo leasingové smlouvy" value={leasingContractNumber} />
                    <HeaderValue label="Číslo pojistné smlouvy" value={insurance.contractNumber} />
                    <HeaderValue label="Klient" value={clientName} />
                    <HeaderValue label="IČO" value={client?.idendificationNumber} />
                  </Grid>

                  <Box>
                    <Text fontWeight="bold" mb={3}>
                      Klient
                    </Text>
                    <Text fontSize="xs" color="gray.500" mb={2}>
                      Plátce / Neplátce DPH
                    </Text>
                    <CheckboxPair
                      value={isVatPayer}
                      onChange={val => {
                        // Plátce → částka bez DPH (÷ koef.), neplátce → s DPH (× koef.) – jako na kroku 1.
                        const currentPrice = methods.getValues('sumInsured')
                        if (currentPrice != null)
                          methods.setValue(
                            'sumInsured',
                            Math.round(val ? currentPrice / VAT_COEF : currentPrice * VAT_COEF),
                            { shouldValidate: true },
                          )
                        methods.setValue('isVatPayer', val, { shouldValidate: true })
                      }}
                      trueLabel="Plátce DPH"
                      falseLabel="Neplátce DPH"
                    />
                  </Box>

                  <Box>
                    <Text fontWeight="bold" mb={3}>
                      Vozidlo
                    </Text>

                    <Grid rowGap={6}>
                      <Grid templateColumns="repeat(3, 1fr)" columnGap={8} rowGap={4}>
                        <InputFormControl
                          name="vin"
                          control={methods.control}
                          label="VIN"
                          maxLength={17}
                          rightElement={
                            <Text fontSize="xs" color="gray.500">
                              {(vin?.length ?? 0)}/17
                            </Text>
                          }
                        />
                        <NumberFormControl
                          name="engineVolume"
                          control={methods.control}
                          label="Objem motoru"
                          inputRightElement={<Text fontSize="sm" color="gray.500">cm³</Text>}
                        />
                        <DateFormControl
                          name="firstRegistrationDate"
                          control={methods.control}
                          label="Datum první registrace"
                        />
                      </Grid>

                      <Grid templateColumns="1fr 1fr" columnGap={8} mt={4}>
                        <NumberFormControl
                          name="sumInsured"
                          control={methods.control}
                          label={`Pojistná částka (${vatLabel})`}
                          inputRightElement={<Text fontSize="sm" color="gray.500">Kč</Text>}
                        />

                        <Box>
                          <HStack gap={2}>
                            <Text fontSize="xs" color="gray.500">
                              {`Časová cena (${vatLabel})`}
                            </Text>

                            {/* Přepočet ocenění – jen když se změnil VIN / objem / datum 1. registrace */}
                            {valuationInputsChanged && (
                              <Icon
                                as={FontAwesomeIcon}
                                icon={faRefresh}
                                color="primary.500"
                                boxSize={3.5}
                                cursor={isLoadingTimePrice ? 'not-allowed' : 'pointer'}
                                opacity={isLoadingTimePrice ? 0.5 : 1}
                                aria-label="Přepočítat časovou cenu"
                                onClick={() => { if (!isLoadingTimePrice) runValuation() }}
                              />
                            )}
                          </HStack>

                          {isLoadingTimePrice ? (
                            <Spinner size="sm" mt={2} />
                          ) : valuationError ? (
                            /* Chyba ocenění – místo hodnoty červený vykřičník, hláška až po kliknutí */
                            <CustomTooltip label={valuationError} trigger="click" hasArrow>
                              <Icon
                                as={FontAwesomeIcon}
                                icon={faExclamationCircle}
                                color="red.500"
                                boxSize={5}
                                mt={2}
                                cursor="pointer"
                              />
                            </CustomTooltip>
                          ) : (
                            <Text fontWeight="bold" mt={2}>
                              {priceFormat(timePrice, 'CZK', undefined, '—')}
                            </Text>
                          )}
                        </Box>
                      </Grid>

                      <Box>
                        <Text fontSize="xs" color="gray.500" mb={2}>
                          Pro kalkulaci použít
                        </Text>

                        <CheckboxPair
                          value={priceOriginId === PRICE_ORIGIN_ACCORDING_HOLDER}
                          onChange={val => methods.setValue(
                            'priceOriginId',
                            val ? PRICE_ORIGIN_ACCORDING_HOLDER : PRICE_ORIGIN_VALUATION,
                            { shouldValidate: true },
                          )}
                          trueLabel="Pojistná cena"
                          falseLabel="Časová cena"
                          falseDisabled={isLoadingTimePrice || timePrice == null}
                        />
                      </Box>

                      <Grid templateColumns="1fr 1fr" columnGap={8} mt={4} alignItems="end">
                        <NumberFormControl
                          name="extraEquipmentPrice"
                          control={methods.control}
                          label={
                            <Flex align="center" gap={1}>
                              Mimořádná výbava (z faktury)
                              <Hint>Cena mimořádné výbavy dle faktury, bez DPH.</Hint>
                            </Flex>
                          }
                          inputRightElement={<Text fontSize="sm" color="gray.500">Kč</Text>}
                        />
                        <Flex bgColor="gray.100" px={4} py={3} align="center" justify="space-between">
                          <Box>
                            <Text fontWeight="medium">Pojistná částka pro kalkulaci</Text>
                            <Text fontSize="xs" color="gray.500">
                              {useTimePrice ? 'Časová cena + výbava' : 'Pojistná cena + výbava'}
                            </Text>
                          </Box>
                          <Text fontWeight="bold" fontSize="lg">
                            {priceFormat(calculationAmount, 'CZK', undefined, '—')}
                          </Text>
                        </Flex>
                      </Grid>
                    </Grid>
                  </Box>

                  <MultipleSelectFormControl
                    name="reasons"
                    control={methods.control}
                    label="Důvod změny novou"
                    isRequired
                    options={reasonOptions}
                    placeholder="Vyberte důvod(y)…"
                  />

                  {showOtherReason && (
                    <TextareaFormControl
                      name="otherReason"
                      control={methods.control}
                      label="Jiný důvod"
                      isRequired
                      rows={3}
                      placeholder="Zadejte důvod…"
                    />
                  )}

                  {methods.formState.errors.root && (
                    <Alert status="error">
                      <AlertIcon />
                      <Text>{methods.formState.errors.root.message}</Text>
                    </Alert>
                  )}
                </Stack>
              </ModalBody>
              <ModalFooter gap={3}>
                <Button variant="ghost" onClick={handleClose} isDisabled={isPending}>
                  Zrušit
                </Button>
                <Button
                  type="submit"
                  isLoading={isPending}
                  isDisabled={!methods.formState.isValid}
                  rightIcon={<FontAwesomeIcon icon={faArrowRight} />}
                >
                  Pokračovat
                </Button>
              </ModalFooter>
            </form>
          </FormProvider>
        </ModalContent>
      </Modal>
    </>
  )
}
